import type { Money } from './money.js';

export const REFUND_STATUSES = ['pending', 'approved', 'paid', 'cancelled'] as const;
export type RefundStatus = (typeof REFUND_STATUSES)[number];

/**
 * Where a refund came from. A cancellation that chose `refund` owes back what a
 * confirmed match had collected; a person owes back part of a line's remainder
 * — a visible overpayment; a verified provider refund was paid out of the
 * capture before anyone saw it, and has no requester.
 */
export const REFUND_ORIGINS = ['cancellation', 'line', 'provider'] as const;
export type RefundOrigin = (typeof REFUND_ORIGINS)[number];

export function refundOrigin(refund: {
  readonly sourceReconciliationId: string | null;
  readonly requestedByUserId: string | null;
}): RefundOrigin {
  if (refund.requestedByUserId === null) return 'provider';
  return refund.sourceReconciliationId === null ? 'line' : 'cancellation';
}

/**
 * What a person may say an outgoing line is. A credit is classified by what it
 * pays — a donor's payment or a card settlement — and a debit arrives `other`
 * unless a statement profile said more, so a refund's payout or a transfer's
 * debit is named by hand before it is used —
 * docs/plans/05-finance-reconciliation.md §5.8.
 */
export const OUTGOING_CLASSIFICATIONS = [
  'refund_payout',
  'internal_transfer',
  'fee',
  'other',
] as const;
export type OutgoingClassification = (typeof OUTGOING_CLASSIFICATIONS)[number];

/** A line as the refund rules read it. */
export type RefundLine = {
  readonly organizationId: string;
  readonly direction: 'credit' | 'debit';
  readonly classification: string;
  readonly excluded: boolean;
  readonly allocatedMinor: bigint;
  readonly amount: Money;
};

export type RefundRequestRefusal =
  | 'REFUND_LINE_NOT_REFUNDABLE'
  | 'REFUND_CURRENCY_MISMATCH'
  | 'REFUND_EXCEEDS_REMAINDER';

/**
 * Whether part of a line's remainder can be owed back: only a donor's payment
 * that is not excluded, in its own currency, and never more than nobody has
 * matched yet — the remainder a T2 match leaves visible is the ordinary case.
 * A card capture is allocated whole to its donation, so it never has one.
 */
export function refundRequestRefusal(line: RefundLine, amount: Money): RefundRequestRefusal | null {
  if (line.direction !== 'credit' || line.classification !== 'donor_payment' || line.excluded) {
    return 'REFUND_LINE_NOT_REFUNDABLE';
  }
  if (amount.currency !== line.amount.currency) return 'REFUND_CURRENCY_MISMATCH';
  return amount.amountMinor > line.amount.amountMinor - line.allocatedMinor
    ? 'REFUND_EXCEEDS_REMAINDER'
    : null;
}

export const REFUND_PAYOUT_INELIGIBILITY = [
  'other_organization',
  'not_outgoing',
  'not_refund_payout',
  'excluded',
] as const;
export type RefundPayoutIneligibility = (typeof REFUND_PAYOUT_INELIGIBILITY)[number];

export type RefundPayoutRefusal =
  | { readonly reason: 'REFUND_PAYOUT_LINE_NOT_ELIGIBLE'; readonly why: RefundPayoutIneligibility }
  | { readonly reason: 'REFUND_PAYOUT_AMOUNT_MISMATCH' }
  | { readonly reason: 'REFUND_PAYOUT_LINE_ALLOCATED' };

/**
 * Whether an outgoing line pays a refund: a non-excluded debit of the refund's
 * organization, classified `refund_payout`, for exactly the refund's amount and
 * currency, that nothing has taken yet. It is allocated whole, so it pays one
 * refund and no other.
 */
export function refundPayoutRefusal(
  refund: { readonly organizationId: string; readonly amount: Money },
  line: RefundLine,
): RefundPayoutRefusal | null {
  const ineligible = (why: RefundPayoutIneligibility) =>
    ({ reason: 'REFUND_PAYOUT_LINE_NOT_ELIGIBLE', why }) as const;
  if (line.organizationId !== refund.organizationId) return ineligible('other_organization');
  if (line.direction !== 'debit') return ineligible('not_outgoing');
  if (line.excluded) return ineligible('excluded');
  if (line.classification !== 'refund_payout') return ineligible('not_refund_payout');
  if (
    line.amount.currency !== refund.amount.currency ||
    line.amount.amountMinor !== refund.amount.amountMinor
  ) {
    return { reason: 'REFUND_PAYOUT_AMOUNT_MISMATCH' };
  }
  return line.allocatedMinor === 0n ? null : { reason: 'REFUND_PAYOUT_LINE_ALLOCATED' };
}
